import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/base/badges/badges'
import { getBackendVersionKind, getPreviewPrNumber, type BackendVersionKind } from '@/lib/backendVersion'
import { cn } from '@/lib/utils'

interface BackendVersionBadgeProps {
  version: string | null | undefined
  className?: string
}

const BADGE_COLOR: Record<BackendVersionKind, 'warning' | 'purple' | 'blue'> = {
  rc: 'warning',
  preview: 'purple',
  smoke: 'blue',
}

/**
 * Admin-only marker for conversations handled by a non-release backend:
 * a release candidate (canary traffic split or LB override), a PR preview,
 * or a smoke-test build on the dev environment.
 * Renders nothing for production releases and unknown versions.
 */
export function BackendVersionBadge({ version, className }: BackendVersionBadgeProps) {
  const { t } = useTranslation()
  const kind = getBackendVersionKind(version)
  if (!kind) return null

  const prNumber = kind === 'preview' ? getPreviewPrNumber(version) : null
  const label = prNumber
    ? t('resources.conversations.backend_version.preview_pr', { pr: prNumber })
    : t(`resources.conversations.backend_version.${kind}`)

  return (
    <span
      className={cn('inline-flex', className)}
      title={t('resources.conversations.backend_version.tooltip', { version })}
    >
      <Badge size="sm" color={BADGE_COLOR[kind]}>
        {label}
      </Badge>
    </span>
  )
}

export default BackendVersionBadge
